import { doc, setDoc, updateDoc, serverTimestamp, increment, addDoc, collection } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import { User } from 'firebase/auth';

interface UserActivity {
  userId: string;
  displayName: string;
  email: string;
  currentPage: string;
  lastActive: Date;
  userAgent: string;
  isActive: boolean;
}

interface ActivityHistoryEntry {
  userId: string;
  displayName: string;
  email: string;
  pageName: string;
  pagePath: string;
  timestamp: Date;
  sessionId: string;
  userAgent: string;
  timeSpent?: number; // in milliseconds
  action: 'page_visit' | 'page_exit' | 'login' | 'logout';
}

interface PageAnalytics {
  pageName: string;
  totalVisits: number;
  totalTimeSpent: number;
  lastVisited: Date;
}

class UserActivityService {
  private currentPage: string = '';
  private pageStartTime: number = 0;
  private activityInterval: NodeJS.Timeout | null = null;
  private currentUser: User | null = null;
  private sessionId: string = '';

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  // Record activity history entry
  private async recordActivityHistory(
    action: 'page_visit' | 'page_exit' | 'login' | 'logout',
    pagePath?: string,
    timeSpent?: number
  ) {
    if (!this.currentUser) {
      return;
    }

    try {
      const historyEntry: Partial<ActivityHistoryEntry> = {
        userId: this.currentUser.uid,
        displayName: this.currentUser.displayName || 'Unknown User',
        email: this.currentUser.email || 'unknown@example.com',
        pageName: pagePath ? this.getPageName(pagePath) : (action === 'login' || action === 'logout' ? action : this.getPageName(this.currentPage)),
        pagePath: pagePath || this.currentPage || window.location.pathname,
        timestamp: serverTimestamp() as any,
        sessionId: this.sessionId,
        userAgent: navigator.userAgent,
        action,
        timeSpent
      };

      await addDoc(collection(db, 'userActivityHistory'), historyEntry);
    } catch (error) {
      // Silently handle errors in production
    }
  }

  // Initialize tracking for a user
  async initializeTracking(user: User) {
    this.currentUser = user;
    this.sessionId = this.generateSessionId();
    
    // Record login event
    await this.recordActivityHistory('login');
    
    this.startActivityTracking();
    this.setupPageTracking();
  }

  // Heartbeat while the tab is visible. Every open sidebar receives each heartbeat, and
  // the sidebar counts someone as online for 3 minutes after their last one.
  private startActivityTracking() {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
    }

    this.updateUserActivity();

    this.activityInterval = setInterval(() => {
      if (document.hidden) return;
      this.updateUserActivity();
    }, 60000);
  }

  // Update user activity in Firebase
  private async updateUserActivity() {
    if (!this.currentUser) return;

    try {
      const userActivityRef = doc(db, 'userActivity', this.currentUser.uid);
      
      const activityData: Partial<UserActivity> = {
        userId: this.currentUser.uid,
        displayName: this.currentUser.displayName || 'Unknown User',
        email: this.currentUser.email || 'unknown@example.com',
        currentPage: this.currentPage || window.location.pathname,
        lastActive: serverTimestamp() as any,
        userAgent: navigator.userAgent,
        isActive: true
      };

      await setDoc(userActivityRef, activityData, { merge: true });
    } catch (error) {
      // Silently handle errors in production
    }
  }

  // Mark user as offline when they log out
  async markUserOffline() {
    if (!this.currentUser) return;

    try {
      const userActivityRef = doc(db, 'userActivity', this.currentUser.uid);
      
      // Record logout activity
      await this.recordActivityHistory('logout');
      
      // Mark as offline in the activity document
      const activityData: Partial<UserActivity> = {
        userId: this.currentUser.uid,
        displayName: this.currentUser.displayName || 'Unknown User',
        email: this.currentUser.email || 'unknown@example.com',
        currentPage: this.currentPage || window.location.pathname,
        lastActive: serverTimestamp() as any,
        userAgent: navigator.userAgent,
        isActive: false
      };

      await setDoc(userActivityRef, activityData, { merge: true });
    } catch (error) {
      // Silently handle errors in production
    }
  }



  // Track page visits and time spent
  private setupPageTracking() {
    // Track initial page load
    this.trackPageVisit(window.location.pathname);

    // Track page changes (for SPAs)
    window.addEventListener('popstate', () => {
      this.trackPageVisit(window.location.pathname);
    });

    // Track before page unload
    window.addEventListener('beforeunload', () => {
      this.trackPageExit();
    });

    // Track visibility changes (when user switches tabs)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.trackPageExit();
      } else {
        this.trackPageVisit(window.location.pathname);
      }
    });
  }

  // Track when user visits a page
  async trackPageVisit(pagePath: string) {
    // If switching pages, track exit from previous page
    if (this.currentPage && this.currentPage !== pagePath) {
      await this.trackPageExit();
    }

    this.currentPage = pagePath;
    this.pageStartTime = Date.now();

    // Record page visit in history
    await this.recordActivityHistory('page_visit', pagePath);

    // Update current page in user activity
    if (this.currentUser) {
      this.updateUserActivity();
    }

    // Increment page visit count
    try {
      const sanitizedPath = this.sanitizePagePath(pagePath);
      const pageName = this.getPageName(pagePath);
      
      const pageRef = doc(db, 'pageAnalytics', sanitizedPath);
      await setDoc(pageRef, {
        pageName: pageName,
        totalVisits: increment(1),
        lastVisited: serverTimestamp()
      }, { merge: true });
    } catch (error) {
      // Silently handle errors
    }
  }

  // Track when user exits a page
  async trackPageExit() {
    if (!this.currentPage || !this.pageStartTime) return;

    const timeSpent = Date.now() - this.pageStartTime;
    
    // Only track if user spent more than 5 seconds on page
    if (timeSpent < 5000) return;

    try {
      // Record page exit in history
      await this.recordActivityHistory('page_exit', this.currentPage, timeSpent);

      const pageRef = doc(db, 'pageAnalytics', this.sanitizePagePath(this.currentPage));
      await updateDoc(pageRef, {
        totalTimeSpent: increment(timeSpent)
      });
    } catch (error) {
      // Silently handle errors
    }
  }

  // Convert page path to readable name
  private getPageName(path: string): string {
    const pageNames: { [key: string]: string } = {
      '/': 'Dashboard',
      '/dashboard': 'Dashboard',
      '/profile': 'Profile',
      '/settings': 'Settings',
      '/commissions': 'Commissions',
      '/commissions-admin': 'Commissions Admin',
      '/tasks': 'Tasks',
      '/pos': 'POS',
      '/pos-pin': 'POS PIN',
      '/jewelry': 'Jewelry Request',
      '/jewelry-requests': 'Jewelry Requests',
      '/request-log': 'Request Log',
      '/interactions': 'Interactions',
      '/log-interaction': 'Log Interaction',
      '/reports': 'Reports',
      '/marketing': 'Marketing',
      '/content-calendar': 'Content Calendar',
      '/email-marketing': 'Email Marketing',
      '/user-management': 'User Management',
      '/template': 'Template',
      '/photo-log': 'Photo Log',
      '/returns': 'Returns',
      '/master-inventory': 'Master Inventory',
      '/shopify-orders': 'Shopify Orders',
      '/watch-history': 'Watch History',
      '/sku-history': 'SKU History',
      '/gem-id': 'GEM ID',
      '/cco-training': 'CCO Training',
      '/cco-repairs': 'CCO Repairs',
      '/buy-trade': 'Buy / Trade-In',
      '/consignments': 'Consignments',
      '/content-creation': 'Content Creation',
      '/then-now-next': 'Then Now Next',
      '/fin-comms': 'Fin Comms',
      '/template-training': 'Template Training',
      '/template-training-admin': 'Template Training Admin',
      '/group-calendar': 'Group Calendar',
      '/finChats': 'Fin Chats'
    };

    return pageNames[path] || path.replace(/^\//, '').replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  // Sanitize page path for use as Firebase document ID
  private sanitizePagePath(path: string): string {
    return path.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/^_+|_+$/g, '') || 'root';
  }

  // Mark user as inactive when they leave
  async markUserInactive() {
    if (!this.currentUser) return;

    try {
      const userActivityRef = doc(db, 'userActivity', this.currentUser.uid);
      await updateDoc(userActivityRef, {
        isActive: false,
        lastActive: serverTimestamp()
      });
    } catch (error) {
      // Silently handle errors in production
    }
  }

  // Clean up tracking
  cleanup() {
    if (this.activityInterval) {
      clearInterval(this.activityInterval);
    }
    this.trackPageExit();
    
    // Mark user as offline and record logout
    this.markUserOffline();
  }

  // Get user activity history
  async getUserActivityHistory(userId: string, limit: number = 50) {
    try {
      const { query, collection, orderBy, where, getDocs, limit: limitQuery } = await import('firebase/firestore');
      
      const historyQuery = query(
        collection(db, 'userActivityHistory'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limitQuery(limit)
      );

      const querySnapshot = await getDocs(historyQuery);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));
      
      return results;
    } catch (error) {
      return [];
    }
  }

  // Get comprehensive user activity stats
  async getUserActivityStats(userId: string) {
    try {
      const { query, collection, orderBy, where, getDocs, limit: limitQuery } = await import('firebase/firestore');
      
      // Get recent activity history
      const historyQuery = query(
        collection(db, 'userActivityHistory'),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limitQuery(100)
      );

      const historySnapshot = await getDocs(historyQuery);
      const history = historySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || new Date()
      }));

      // Calculate stats
      const pageVisits = history.filter(h => h.action === 'page_visit').length;
      const totalSessions = new Set(history.map(h => h.sessionId)).size;
      const totalTimeSpent = history
        .filter(h => h.timeSpent)
        .reduce((acc, h) => acc + (h.timeSpent || 0), 0);

      // Get most visited pages
      const pageVisitCounts: { [key: string]: number } = {};
      history.filter(h => h.action === 'page_visit').forEach(h => {
        pageVisitCounts[h.pageName] = (pageVisitCounts[h.pageName] || 0) + 1;
      });

      const mostVisitedPages = Object.entries(pageVisitCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([page, count]) => ({ page, count }));

      return {
        history,
        stats: {
          pageVisits,
          totalSessions,
          totalTimeSpent,
          mostVisitedPages,
          averageSessionTime: totalSessions > 0 ? totalTimeSpent / totalSessions : 0
        }
      };
    } catch (error) {
      // Silently handle errors
      return {
        history: [],
        stats: {
          pageVisits: 0,
          totalSessions: 0,
          totalTimeSpent: 0,
          mostVisitedPages: [],
          averageSessionTime: 0
        }
      };
    }
  }
}

export const userActivityService = new UserActivityService(); 